'use client'

import { cn } from '@/lib/utils'
import { type Locale, locales, useLanguageStore } from '@/stores/language-store'
import { Menu, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { analyticsItems, navItems } from './header'

const localeLabels: Record<Locale, string> = {
	en: 'English',
	ko: '한국어',
	zh: '中文',
}

const localeFlags: Record<Locale, string> = {
	en: '🇺🇸',
	ko: '🇰🇷',
	zh: '🇨🇳',
}

interface MobileMenuProps {
	mobileSearchExpanded?: boolean
}

export function MobileMenu({ mobileSearchExpanded = false }: MobileMenuProps) {
	const [isOpen, setIsOpen] = useState(false)
	const pathname = usePathname()
	const t = useTranslations('nav')
	const { locale, setLocale } = useLanguageStore()

	// Combine all items for mobile menu display
	const allMobileNavItems = [
		navItems[0], // Trade
		...analyticsItems, // Trending, Top Traders, Activity
		...navItems.slice(1), // Portfolio, Earn
	]

	const toggleMenu = () => setIsOpen(!isOpen)
	const closeMenu = () => setIsOpen(false)

	return (
		<div className={cn('relative md:hidden', mobileSearchExpanded && 'hidden')}>
			{/* Hamburger button - visible on mobile only */}
			<button
				type="button"
				onClick={toggleMenu}
				className="flex items-center justify-center p-2"
				aria-label="Toggle menu"
			>
				{isOpen ? (
					<X className="h-[24px] w-[24px] text-white" />
				) : (
					<Menu className="h-[24px] w-[24px] text-white" />
				)}
			</button>

			{/* Mobile menu dropdown */}
			{isOpen && (
				<>
					{/* Overlay */}
					<div className="fixed inset-0 z-40" onClick={closeMenu} />

					{/* Menu dropdown */}
					<nav className="-right-[120px] absolute top-full z-50 mt-2 min-w-[200px] max-w-[calc(100vw-32px)] rounded-[12px] border border-white/10 bg-brand-dark-light lg:right-0">
						<div className="flex flex-col">
							{allMobileNavItems.map(item => {
								const isActive =
									item.href === '/' ? pathname === '/' : pathname.startsWith(item.href)

								return (
									<Link
										key={item.key}
										href={item.href}
										onClick={closeMenu}
										className={cn(
											'border-white/10 border-b px-4 py-3 font-inter text-[14px] text-white leading-[100%]',
											isActive ? 'bg-white/5 font-medium' : 'font-normal opacity-75'
										)}
									>
										{t(item.key)}
									</Link>
								)
							})}
							<div className="border-white/10 border-t px-4 py-3">
								<div className="flex items-center gap-2">
									{locales.map(loc => (
										<button
											key={loc}
											type="button"
											onClick={() => setLocale(loc)}
											className={cn(
												'flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1.5 text-sm text-white transition-colors',
												locale === loc
													? 'bg-brand-blue/20 font-medium'
													: 'opacity-75 hover:opacity-100'
											)}
										>
											<span>{localeFlags[loc]}</span>
											<span>{localeLabels[loc]}</span>
										</button>
									))}
								</div>
							</div>
						</div>
					</nav>
				</>
			)}
		</div>
	)
}
